import { useState } from 'react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { 
  UtensilsCrossed, Receipt, Bell, ChefHat, Check, 
  Clock, List, FileText
} from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import OrderListDialog from './OrderListDialog';

// Order status config
const ORDER_STATUS = {
  pending: { label: 'Pending', color: 'bg-red-500', icon: Bell },
  preparing: { label: 'Preparing', color: 'bg-amber-500', icon: ChefHat },
  ready: { label: 'Ready', color: 'bg-green-500', icon: Check },
  served: { label: 'Served', color: 'bg-blue-500', icon: UtensilsCrossed }
};

const RunningTablesBar = ({
  tableOrders,
  tables,
  restaurantId,
  onOpenBilling,
  onGenerateBill,
  onRefreshOrders,
  allOrders = []
}) => {
  const [selectedTableData, setSelectedTableData] = useState(null);
  const [ordersListOpen, setOrdersListOpen] = useState(false);

  const getTableTotal = (tableId) => (tableOrders[tableId] || []).reduce((sum, o) => sum + (o.total_amount || 0), 0);
  
  const getTableOrderStatus = (tableId) => {
    const orders = tableOrders[tableId] || [];
    if (orders.length === 0) return null;
    
    const statusPriority = ['pending', 'preparing', 'ready', 'served'];
    for (const status of statusPriority) {
      if (orders.some(o => o.status === status)) return status;
    }
    return 'pending';
  };

  const getTableOrderStats = (tableId) => {
    const orders = tableOrders[tableId] || [];
    return {
      pending: orders.filter(o => o.status === 'pending').length,
      preparing: orders.filter(o => o.status === 'preparing').length,
      ready: orders.filter(o => o.status === 'ready').length,
      served: orders.filter(o => o.status === 'served').length,
      total: orders.length
    };
  };

  const allTableOrders = Object.entries(tableOrders).flatMap(([tableId, orders]) => {
    const table = tables.find(t => t.id === tableId);
    return orders.map(o => ({ ...o, table }));
  });

  const canGenerateBill = (tableId) => {
    const orders = tableOrders[tableId] || [];
    return orders.length > 0 && orders.every(o => o.status === 'served');
  };

  const handleTableClick = (tableId) => {
    const table = tables.find(t => t.id === tableId);
    const orders = tableOrders[tableId] || [];
    setSelectedTableData({ tableId, table, orders });
  };

  const hasActiveOrders = Object.keys(tableOrders).length > 0;

  return (
    <>
      <div className="bg-gradient-to-r from-slate-800 to-slate-900 border-b border-slate-700" data-testid="running-tables-bar">
        <div className="flex items-center gap-2 px-3 py-2">
          {/* Table Tabs */}
          <div className="flex-1 overflow-x-auto">
            <div className="flex items-center gap-2">
              <span className="text-sm font-medium text-slate-300 mr-2 whitespace-nowrap">
                <UtensilsCrossed className="h-4 w-4 inline mr-1" />
                Active Tables:
              </span>
              
              {Object.entries(tableOrders).map(([tableId, orders]) => {
                const table = tables.find(t => t.id === tableId);
                const status = getTableOrderStatus(tableId);
                const statusInfo = ORDER_STATUS[status] || ORDER_STATUS.pending;
                const StatusIcon = statusInfo.icon;
                const stats = getTableOrderStats(tableId);
                const allServed = canGenerateBill(tableId);
                
                return (
                  <Button 
                    key={tableId}
                    variant="outline"
                    size="sm"
                    className={`
                      bg-slate-700/50 border-slate-600 text-slate-200 hover:bg-slate-700
                      ${allServed ? 'ring-2 ring-green-400 ring-offset-1 ring-offset-slate-800' : ''}
                      transition-all flex-shrink-0
                    `}
                    onClick={() => handleTableClick(tableId)}
                    data-testid={`table-tab-${tableId}`}
                  >
                    <div className="flex items-center gap-2">
                      <span className="font-bold">T{table?.table_number}</span>
                      <Badge className={`${statusInfo.color} text-white text-xs px-1.5 py-0`}>
                        <StatusIcon className="h-3 w-3 mr-0.5" />
                        {stats.total}
                      </Badge>
                      <span className="text-xs opacity-80">₹{getTableTotal(tableId).toFixed(0)}</span>
                    </div>
                  </Button>
                );
              })}
              
              {!hasActiveOrders && (
                <span className="text-sm text-slate-500 italic">No active tables</span>
              )}
            </div>
          </div>

          {/* Order History Button - Always visible */}
          <Button
            variant="outline"
            size="sm"
            className="bg-amber-600 hover:bg-amber-700 border-amber-500 text-white flex-shrink-0"
            onClick={() => setOrdersListOpen(true)}
            data-testid="order-history-btn"
          >
            <Receipt className="h-4 w-4 mr-1" />
            Order History
            {hasActiveOrders && (
              <Badge className="ml-1 bg-white text-amber-600 text-xs">{allTableOrders.length}</Badge>
            )}
          </Button>
        </div>
      </div>

      {/* Table Details Dialog */}
      <Dialog open={!!selectedTableData} onOpenChange={(open) => !open && setSelectedTableData(null)}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <UtensilsCrossed className="h-5 w-5" />
              Table {selectedTableData?.table?.table_number} Orders
            </DialogTitle>
          </DialogHeader>
          
          {selectedTableData && (
            <div className="space-y-3">
              {/* Status Summary */}
              <div className="flex flex-wrap gap-2">
                {getTableOrderStats(selectedTableData.tableId).pending > 0 && (
                  <Badge className="bg-red-500/20 text-red-600">
                    <Bell className="h-3 w-3 mr-1" /> {getTableOrderStats(selectedTableData.tableId).pending} Pending
                  </Badge>
                )}
                {getTableOrderStats(selectedTableData.tableId).preparing > 0 && (
                  <Badge className="bg-amber-500/20 text-amber-600">
                    <ChefHat className="h-3 w-3 mr-1" /> {getTableOrderStats(selectedTableData.tableId).preparing} Preparing
                  </Badge>
                )}
                {getTableOrderStats(selectedTableData.tableId).ready > 0 && (
                  <Badge className="bg-green-500/20 text-green-600">
                    <Check className="h-3 w-3 mr-1" /> {getTableOrderStats(selectedTableData.tableId).ready} Ready
                  </Badge>
                )}
                {getTableOrderStats(selectedTableData.tableId).served > 0 && (
                  <Badge className="bg-blue-500/20 text-blue-600">
                    <UtensilsCrossed className="h-3 w-3 mr-1" /> {getTableOrderStats(selectedTableData.tableId).served} Served
                  </Badge>
                )}
              </div>
              
              {/* Orders List */}
              <div className="space-y-2 max-h-64 overflow-y-auto">
                {selectedTableData.orders.map((order, idx) => {
                  const orderStatus = ORDER_STATUS[order.status] || ORDER_STATUS.pending;
                  return (
                    <div key={order.id || idx} className="bg-gray-50 rounded-lg p-3 border">
                      <div className="flex items-center justify-between mb-2">
                        <span className="text-sm text-gray-500">
                          #{order.id?.slice(-6).toUpperCase() || idx + 1}
                        </span>
                        <Badge className={`${orderStatus.color} text-white text-xs`}>
                          {orderStatus.label}
                        </Badge>
                      </div>
                      <div className="text-sm">
                        {order.items?.slice(0, 3).map((item, i) => (
                          <div key={i} className="flex justify-between text-gray-600">
                            <span>{item.quantity}x {item.item_name}</span>
                            <span>₹{(item.price * item.quantity).toFixed(0)}</span>
                          </div>
                        ))}
                        {order.items?.length > 3 && (
                          <div className="text-xs text-gray-400">
                            +{order.items.length - 3} more items
                          </div>
                        )}
                      </div>
                      <div className="text-right font-bold mt-2">
                        ₹{(order.total_amount || 0).toFixed(0)}
                      </div>
                    </div>
                  );
                })}
              </div>
              
              {/* Total and Actions */}
              <div className="border-t pt-3">
                <div className="flex justify-between items-center mb-3">
                  <span className="font-bold">Total</span>
                  <span className="text-xl font-bold text-primary">
                    ₹{getTableTotal(selectedTableData.tableId).toFixed(0)}
                  </span>
                </div>
                
                <div className="flex gap-2">
                  {canGenerateBill(selectedTableData.tableId) ? (
                    <Button 
                      className="flex-1 bg-green-600 hover:bg-green-700"
                      onClick={() => {
                        onGenerateBill?.(selectedTableData.table);
                        setSelectedTableData(null);
                      }}
                    >
                      <FileText className="h-4 w-4 mr-1" />
                      Generate Bill
                    </Button>
                  ) : (
                    <Button 
                      variant="outline"
                      className="flex-1"
                      onClick={() => {
                        onOpenBilling(selectedTableData.table);
                        setSelectedTableData(null);
                      }}
                    >
                      <Receipt className="h-4 w-4 mr-1" />
                      View / Bill
                    </Button>
                  )}
                </div>
                
                {canGenerateBill(selectedTableData.tableId) && (
                  <p className="text-xs text-green-600 mt-2 text-center">
                    ✓ All orders served - Ready for billing
                  </p>
                )}
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* All Orders List Dialog */}
      <OrderListDialog
        open={ordersListOpen}
        onOpenChange={setOrdersListOpen}
        restaurantId={restaurantId}
        onOrderStatusChange={onRefreshOrders}
      />
    </>
  );
};

export default RunningTablesBar;
